import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, movesOn, tweenChunks } from '../skillUsage';

export const graphicWipeHandoffSkill: CodeoSkill = {
    name: SKILL_NAMES.graphicWipeHandoff,
    kind: 'codeo',
    description:
        'Hand one line of copy to the next by moving the graphic between them: it travels across ' +
        'the frame, wiping the first line off under its leading edge and uncovering the second ' +
        'from behind its trailing edge, so the layout mirrors from "graphic | line" to ' +
        '"line | graphic" and the graphic itself is the transition.',
    checkLooksFor:
        'two tweens on clip-path — one closing over the outgoing line, one opening on the ' +
        'incoming one — and an element travelling on x',
    documentUsesSkill: check(
        (script) =>
            tweenChunks(script).filter((chunk) => /clipPath|clip-path/.test(chunk)).length >= 2 &&
            movesOn(script, 'x'),
    ),
    reviewNote:
        'A frame caught mid-sweep shows the outgoing line cut off along the edge of the moving ' +
        'graphic and the incoming line only partly uncovered behind it. That is the wipe working, ' +
        'not clipped or obscured copy — judge each line only once the graphic has fully passed it.',
    instructions: `# Graphic Wipe Handoff

Two lines share one graphic, and the second takes over from the first. The scene opens as **graphic | line 1**. The graphic travels across the frame over line 1 and lands as **line 2 | graphic**. Line 1 disappears under the graphic's leading edge as it passes, and line 2 is uncovered from behind its trailing edge. Neither line fades: the graphic's passage *is* the change, so line 2 gets no entrance of its own.

The default for this moment is line 1 fading out and line 2 fading in beside a graphic that sits still. Here the graphic does editorial work instead: it carries the subject from the first statement into the second ("A multinational company" → "operating in over 100 countries").

Use it when a scene has exactly two lines about the one thing the graphic depicts, and that graphic can plausibly travel. When it can't, or the facts number three or more, use \`${SKILL_NAMES.anchoredRelayReveal}\`. When the lines build toward one frame that keeps them both, use \`${SKILL_NAMES.sustainedSceneReveal}\`. When the graphic itself turns into something else, use \`${SKILL_NAMES.elementMorphHandoff}\`.

## Lay out both frames as mirrors

Design the opening frame and the resting frame as two finished stills that mirror each other:

- Both lines sit in slots the same size.
- All three elements share one vertical centre.
- The graphic's travel is the distance between its two positions.

**Sweep the way the copy reads.** The trailing edge uncovers line 2 from the side it passes first. A graphic travelling with the reading direction (rightwards for English) therefore shows line 2's first word first. Travelling against it shows the last word first, and the viewer hears the start of the line while only its end is on screen.

Two edges set the travel, and the example below satisfies both:

- **The trailing edge starts where line 2's copy starts.** Nothing of line 2 is uncovered until the sweep begins. Its clip keeps it hidden, not the graphic, which is usually narrower than the slot.
- **The leading edge ends where line 1's copy ends.** Line 1 is fully erased by the time the graphic lands.

The graphic should be the frame's dominant element, roughly as tall as the copy block is wide. A small icon sliding across a headline reads as a cursor, not a wipe. A round graphic (a globe, a roll, a coin, a dial) fits best: it can roll as it travels.

## Markup

Each line is a slot wrapper holding its copy, and a layout root places both slots against the frame margins. The wrapper carries the clip; line 1's copy carries its entrance. Line 2's wrapper starts at \`opacity: 0\` in CSS. The graphic is the one element placed at a point, so it carries \`data-l5-absolute\` and the script moves it into its starting slot. It also carries \`data-l5-allow-overlap\`, because lying over copy mid-sweep is the design. Put both attributes on the graphic alone, never on the slots or a wrapper around the scene.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-between p-24">
  <div id="gwh-line-2" class="relative shrink-0 flex flex-col justify-center" style="width: 760px; height: 280px; opacity: 0;">
    <h2 style="margin: 0; font-family: var(--l5-font-0), sans-serif; font-size: 72px; font-weight: 700; line-height: 1.05; color: var(--l5-color-0, #374151);">operating in over 100 countries</h2>
  </div>
  <div id="gwh-line-1" class="relative shrink-0 flex flex-col justify-center" style="width: 760px; height: 280px;">
    <h2 style="margin: 0; font-family: var(--l5-font-0), sans-serif; font-size: 72px; font-weight: 700; line-height: 1.05; color: var(--l5-color-0, #374151);">A multinational company</h2>
  </div>
  <div id="gwh-graphic" data-l5-absolute data-l5-allow-overlap style="position: absolute; left: 0; top: 260px; width: 560px; height: 560px;">
    <svg viewBox="0 0 560 560" width="560" height="560" style="display: block; overflow: visible;">
      <g id="gwh-face">
        <circle cx="280" cy="280" r="276" fill="var(--l5-color-1, #6b7280)" fill-opacity="0.35" stroke="var(--l5-color-1, #6b7280)" stroke-width="8"/>
        <circle cx="280" cy="280" r="150" fill="none" stroke="var(--l5-color-0, #374151)" stroke-width="6" opacity="0.6"/>
        <path d="M280 20 V130 M280 430 V540 M20 280 H130 M430 280 H540" stroke="var(--l5-color-0, #374151)" stroke-width="10" stroke-linecap="round"/>
      </g>
    </svg>
  </div>
</div>
\`\`\`

## Timeline code

The graphic's travel and both clips are three tweens with the same start, duration and ease, and each clip edge moves exactly \`TRAVEL\` pixels. Together these lock both edges to the graphic on every frame. Retime or re-ease any one of them and the edge slides off the graphic: letters vanish ahead of it or show through it.

\`\`\`js
const tl = gsap.timeline({ paused: true });

const SCENE_SECONDS = 7.0;
const SETTLE_AT = SCENE_SECONDS - 1.2;
const CUE_LINE_1 = 0.7;
const CUE_LINE_2 = 3.6;
const LEAD = 0.4;

// Read off the laid-out slots, so the travel and both clip edges follow the layout root.
const line1 = document.getElementById('gwh-line-1');
const line2 = document.getElementById('gwh-line-2');
const LINE_1_LEFT = line1.offsetLeft;
const LINE_2_LEFT = line2.offsetLeft;
const SLOT_WIDTH = line1.offsetWidth;
const SLOT_HEIGHT = line1.offsetHeight;
const GRAPHIC_WIDTH = document.getElementById('gwh-graphic').offsetWidth;
const GRAPHIC_START = LINE_2_LEFT;
const GRAPHIC_END = LINE_1_LEFT + SLOT_WIDTH - GRAPHIC_WIDTH;
const TRAVEL = GRAPHIC_END - GRAPHIC_START;
const TRUE_ROLL_TURNS = TRAVEL / (Math.PI * GRAPHIC_WIDTH);
const ROLL_DEGREES = Math.max(1, Math.round(TRUE_ROLL_TURNS)) * 360;

const SWEEP = { duration: 1.1, ease: 'power3.inOut' };
const SWEEP_LEAD = 0.25;
const SWEEP_AT = CUE_LINE_2 - SWEEP_LEAD;

// Each clip edge in its own slot's coordinates. BLEED keeps the clip off ink that hangs past the
// line box; an edge may run past either side of the slot.
const BLEED = 40;
function keepLeftOf(edge) {
    return \`polygon(\${-BLEED}px \${-BLEED}px, \${edge}px \${-BLEED}px, \${edge}px \${SLOT_HEIGHT + BLEED}px, \${-BLEED}px \${SLOT_HEIGHT + BLEED}px)\`;
}
function keepRightOf(edge) {
    return \`polygon(\${edge}px \${-BLEED}px, \${SLOT_WIDTH + BLEED}px \${-BLEED}px, \${SLOT_WIDTH + BLEED}px \${SLOT_HEIGHT + BLEED}px, \${edge}px \${SLOT_HEIGHT + BLEED}px)\`;
}
const LEADING_EDGE = GRAPHIC_START + GRAPHIC_WIDTH - LINE_1_LEFT;
const TRAILING_EDGE = GRAPHIC_START - LINE_2_LEFT;

tl.fromTo('#gwh-line-1 h2', { opacity: 0, y: 24 },
    { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }, CUE_LINE_1 - LEAD);

tl.fromTo('#gwh-graphic', { x: GRAPHIC_START, rotation: 0 },
    { x: GRAPHIC_END, rotation: ROLL_DEGREES, ...SWEEP }, SWEEP_AT);
tl.fromTo('#gwh-line-1', { clipPath: keepRightOf(LEADING_EDGE) },
    { clipPath: keepRightOf(LEADING_EDGE + TRAVEL), ...SWEEP }, SWEEP_AT);
tl.set('#gwh-line-2', { opacity: 1 }, SWEEP_AT);
tl.fromTo('#gwh-line-2', { clipPath: keepLeftOf(TRAILING_EDGE) },
    { clipPath: keepLeftOf(TRAILING_EDGE + TRAVEL), ...SWEEP }, SWEEP_AT);
tl.set('#gwh-line-1', { opacity: 0 }, SWEEP_AT + SWEEP.duration);

tl.fromTo('#gwh-face', { y: 0 },
    { y: -14, duration: SETTLE_AT, ease: 'none' }, 0);
tl.set({}, {}, SCENE_SECONDS);

window.__timelines = { main: tl };
\`\`\`

Three choices in that code are load-bearing:

- **The clip is a \`polygon()\`, not \`inset()\` or a wrapper's \`width\`.** At the ends of the sweep each edge sits outside its slot. A polygon with points outside its box is valid and simply shows nothing there. A negative \`width\` is invalid, so the browser drops it and the line is left half-erased.
- **The clip does the erasing, not the graphic's paint.** Graphics are rarely opaque, as the 0.35 fill here shows. Relying on paint alone, line 1 would show through the graphic for the whole sweep.
- **Each \`tl.set\` opacity switch fires while its line is fully clipped**, line 2's as the sweep starts and line 1's as it ends. The lint and the reviewer judge visibility by opacity, not \`clip-path\`, so these switches are what tell them when each line is on screen. Placed at the sweep's ends, they match what the viewer sees.

## Adapt

- **Copy that reads right to left:** mirror the layout and send the graphic leftwards. Recompute both edges from the graphic's new leading side (its left) and trailing side (its right), and swap \`keepLeftOf\` and \`keepRightOf\`. Negating \`TRAVEL\` alone leaves the clips measuring the wrong side.
- **Sweep duration:** 0.9–1.3s, with an \`inOut\` ease (\`power2\` or \`power3\`). Faster reads as a whip and the copy never registers as being wiped. Slower makes the half-erased frame a pause in its own right.
- **Roll** only a round graphic, so it turns as if rolling along the floor rather than sliding. True rolling is \`TRAVEL / radius\` radians, but the example rounds it to whole turns: a globe, a face or a logo has an up direction, and a part turn lands it on its side for the rest of the scene. Only a graphic that looks the same at any angle (a plain ring, a coin edge) can take the exact figure. A non-round graphic travels without turning, or with a lean of a few degrees that settles out with the ease.
- **Timing:** start the sweep from line 2's cue, at most 0.3s ahead of it. Line 2 is on screen from the sweep's first frame, so an earlier start puts it ahead of its words, and the narration lint flags that. Line 1 needs its reading time, \`max(1.2s, 0.4s + characters / 15)\`, between landing and \`SWEEP_AT\`. Count none of the sweep towards it: the erasing begins partway in.
- **Keep the graphic alive** before and after the sweep with a slow, constant drift on an inner element, or a spin if it looks the same at any angle — a spin tilts anything with an up direction just as a part-turn roll does (\`${SKILL_NAMES.sustainedSceneReveal}\` covers the choice of treatment). End it at \`SETTLE_AT\` so the resting frame holds still for its last second.

## Pitfalls

- **Keep the copy inside the middle half of the graphic's height.** The clip edge is a straight vertical line through the graphic's box edge. Beside the middle of a circle, that is where the visible edge is. Beside its top or bottom, the curve has fallen away, so letters vanish before anything has reached them.
- **One sweep per scene.** Sweeping back again is a ping-pong.
- **No CSS \`transform\` on the graphic or the slots.** GSAP tweens the graphic's \`x\` and \`rotation\` and the copy's \`y\`.
- **Only two lines from the brief.** Don't invent a second line so the technique has something to reveal.`,
};
